import React from 'react';
import { Search, CheckCircle, AlertTriangle, TrendingUp } from 'lucide-react';

export const TrademarkSearch = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-4 py-2 rounded-full mb-6">
              <Search className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-semibold">Comprehensive Search Service</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Comprehensive Trademark <span className="text-yellow-400">Search</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Avoid costly rejections and legal conflicts. Our comprehensive trademark search identifies potential conflicts before you file.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Order Search Report
              </button>
              <button
                onClick={onOpenPopup}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Free Preliminary Check
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
                alt="Trademark search"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Why a Comprehensive Search is <span className="text-red-600">Essential</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Filing a trademark application without a proper search is like driving blindfolded—you're likely to crash. The USPTO rejects approximately 85% of applications that lack a comprehensive search due to conflicts with existing marks.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our comprehensive trademark search goes far beyond simple USPTO database checks. We analyze federal registrations, pending applications, state registrations, common law usage, domain names, and social media handles to identify every possible conflict.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-gray-900 mb-2">Did You Know?</div>
                    <p className="text-gray-700">
                      The USPTO filing fee is non-refundable, even if your application is rejected. A comprehensive search can save you hundreds of dollars in wasted fees and years of legal headaches.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Search */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Search <span className="text-red-600">Coverage</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We search every possible source to ensure your trademark is truly available.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: 'Federal Registrations',
                description: 'All USPTO registered trademarks across all 45 international classes',
                icon: CheckCircle
              },
              {
                title: 'Pending Applications',
                description: 'Active trademark applications that may block your registration',
                icon: CheckCircle
              },
              {
                title: 'State Registrations',
                description: 'State-level trademark registrations in all 50 states',
                icon: CheckCircle
              },
              {
                title: 'Common Law Rights',
                description: 'Unregistered marks in active commercial use',
                icon: CheckCircle
              },
              {
                title: 'Domain Names',
                description: 'Similar domain registrations that could indicate prior use',
                icon: CheckCircle
              },
              {
                title: 'Social Media',
                description: 'Business names and handles on major social platforms',
                icon: CheckCircle
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search Levels */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Search <span className="text-red-600">Packages</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Basic Search',
                  price: 'Free',
                  features: [
                    'USPTO database check',
                    'Identical match search',
                    'Same class only',
                    'Quick turnaround'
                  ],
                  description: 'Perfect for a quick preliminary check'
                },
                {
                  name: 'Standard Search',
                  price: '$99',
                  features: [
                    'Federal & state databases',
                    'Similar mark analysis',
                    'Multiple class search',
                    'Written summary report',
                    'Domain name check'
                  ],
                  description: 'Recommended for most businesses',
                  highlighted: true
                },
                {
                  name: 'Comprehensive Search',
                  price: '$199',
                  features: [
                    'All Standard features',
                    'Common law usage',
                    'Social media analysis',
                    'Detailed legal opinion',
                    'Risk assessment',
                    'Alternative suggestions'
                  ],
                  description: 'Maximum protection and peace of mind'
                }
              ].map((pkg, index) => (
                <div
                  key={index}
                  className={`rounded-xl p-8 shadow-lg transition-all duration-300 hover:-translate-y-2 ${
                    pkg.highlighted
                      ? 'bg-gradient-to-br from-red-600 to-red-700 text-white ring-4 ring-yellow-400'
                      : 'bg-white'
                  }`}
                >
                  <h3 className={`text-2xl font-bold mb-2 ${pkg.highlighted ? 'text-white' : 'text-gray-900'}`}>
                    {pkg.name}
                  </h3>
                  <div className={`text-4xl font-bold mb-4 ${pkg.highlighted ? 'text-white' : 'text-red-600'}`}>
                    {pkg.price}
                  </div>
                  <p className={`mb-6 text-sm ${pkg.highlighted ? 'text-white/90' : 'text-gray-600'}`}>
                    {pkg.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${pkg.highlighted ? 'text-yellow-400' : 'text-green-600'}`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={onOpenPopup}
                    className={`w-full py-3 rounded-lg font-semibold transition-all ${
                      pkg.highlighted
                        ? 'bg-white text-red-600 hover:bg-gray-100'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    Order Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our <span className="text-red-600">Search Service?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: TrendingUp,
                title: 'Higher Approval Rate',
                description: 'Identify conflicts early and adjust your strategy for maximum success'
              },
              {
                icon: AlertTriangle,
                title: 'Avoid Rejections',
                description: 'Save time and money by preventing likely USPTO office actions'
              },
              {
                icon: CheckCircle,
                title: 'Expert Analysis',
                description: 'Professional trademark attorneys review and interpret search results'
              },
              {
                icon: Search,
                title: 'Comprehensive Coverage',
                description: 'We search sources that automated tools and DIY searches miss'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start with a Free Preliminary Search
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get a quick assessment of your trademark's availability. Our team will review the results and recommend the best path forward.
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300"
          >
            Get Free Search Now
          </button>
        </div>
      </section>
    </div>
  );
};